import { useTypedDispatch } from "App/Store";
import { SelectOptions } from "core/models/Settings";
import { convertFileToBase64 } from "core/utils/Image";
import { maxLength, onlyNumber } from "core/utils/Normalizer";
import { useCallback, useEffect, useState } from "react";
import {
  Field,
  FieldArray,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from "redux-form";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { Button, Form } from "rsuite";
import InputWithLabel from "shared/components/redux-form/InputWithLabel";
import Select from "shared/components/redux-form/Select";
import MultiSelect from "shared/components/redux-form/MultiSelect";
import UploadWithPreview from "shared/components/UploadWithPreview";
import validate from "./validate";
import Arrow from "shared/Icons/Arrow";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import StoreGameInfo from "./StoreGameInfo";
import StoreDescription from "./StoreDescription";
import StorePriceDenomination from "./StorePriceDenomination";
import { Store } from "core/models/Store";
import { getCountries } from "core/services/Common";
import { getBrands } from "core/services/Brand";
import { getCategories } from "core/services/Category";
import { PLATFORM_OPTIONS } from "core/constants/Store";
import { useSelector } from "react-redux";

export interface StepImage {
  file: File | null;
  preview: string;
}
interface AddStoreFormProps {
  onAddStore(storeData: any): void;
  isEditMode: boolean;
  store?: Store | null;
}

const COUNTRY_TYPE: SelectOptions[] = [
  {
    label: "India",
    value: "india",
  },
  {
    label: "UAE",
    value: "uae",
  },
  {
    label: "Global",
    value: "global",
  },
];

const PLATFORM_TYPE = PLATFORM_OPTIONS;

const CARD_TYPE: SelectOptions[] = [
  {
    label: "Top Up",
    value: "topup",
  },
  {
    label: "Gift Card",
    value: "giftcard",
  },
];

const optionsReq = [
  { label: "Is Popular", value: true },
  { label: "Not Popular", value: false },
];

const optionStock = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

// JSON array fields (brand regions / categoryIds) may come back as an array or
// (for legacy rows) a JSON string.
function parseJsonArray<T = string>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "string" && value) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

type FinalProps = InjectedFormProps<{}, AddStoreFormProps> & AddStoreFormProps;

const handleSubmitFail = () => {
  setTimeout(() => {
    const firstElement = document.getElementsByClassName(
      "rs-error-message-wrapper"
    )[0];
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
};

const AddStoreForm = ({
  handleSubmit,
  onAddStore,
  initialize,
  change,
  initialized,
  submitFailed,
  isEditMode,
  store,
  reset,
}: FinalProps) => {
  const [storeBanner, setStoreBanner] = useState("");
  const [storeIcon, setStoreIcon] = useState("");

  const [storeBannerFile, setStoreBannerFile] = useState<File | null>(null);
  const [storeIconFile, setStoreIconFile] = useState<File | null>(null);
  const [stepImages, setStepImages] = useState<Record<number, StepImage>>({});
  const [countryOptions, setCountryOptions] = useState<SelectOptions[]>();

  const [storeBannerError, setStoreBannerError] = useState("");
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const dispatch = useTypedDispatch();
  const history = useHistory();
  const selector = formValueSelector("add-store");
  const selectedType = useSelector((state) => selector(state, "type"));
  const selectedBrandId = useSelector((state) => selector(state, "brandId"));

  const brandOptions: SelectOptions[] = brands.map((b: any) => ({
    label: b.name,
    value: b.id,
  }));

  const selectedBrand = brands.find((b: any) => b.id === selectedBrandId);
  const brandRegions = parseJsonArray(selectedBrand?.regions);
  const brandCategoryIds = parseJsonArray<number>(selectedBrand?.categoryIds);
  const brandPlatforms = parseJsonArray(selectedBrand?.platforms);

  // The brand scopes which regions a product may use: a "Global" brand (or one
  // with no regions set) allows every region; otherwise only the brand's own
  // regions are offered.
  const isGlobalBrand =
    !selectedBrand || !brandRegions.length || brandRegions.includes("Global");
  const regionOptions: SelectOptions[] = isGlobalBrand
    ? countryOptions ?? []
    : brandRegions.map((r) => ({ label: r, value: r }));

  // Platforms are likewise scoped to the selected brand: with no brand (or a
  // brand that lists no platforms) every platform is offered, otherwise only the
  // brand's platforms.
  const platformOptions: SelectOptions[] =
    !selectedBrand || !brandPlatforms.length
      ? PLATFORM_TYPE
      : PLATFORM_TYPE.filter((p) => brandPlatforms.includes(p.value as string));

  // Categories are filtered by the selected product type (their `group`/lens),
  // and — when a brand is selected — narrowed to the categories that brand
  // offers, so a product only picks from its brand's categories.
  const categoryOptions: SelectOptions[] = categories
    .filter((c: any) => !selectedType || !c.group || c.group === selectedType)
    .filter(
      (c: any) => !brandCategoryIds.length || brandCategoryIds.includes(c.id)
    )
    .map((c: any) => ({ label: c.name, value: c.id }));

  const getCountriesOptions = useCallback(async () => {
    const countryData = await getCountries();
    if (countryData?.status && countryData?.data?.countries?.length) {
      const mapped = [
        ...countryData.data.countries.map((c: any) => ({
          label: c.label,
          value: c.label,
        })),
        {
          label: "Global",
          value: "Global",
        },
      ];

      setCountryOptions(mapped);
    }
  }, []);

  useEffect(() => {
    getCountriesOptions();
  }, [getCountriesOptions]);

  const loadBrandsAndCategories = useCallback(async () => {
    const [brandRes, categoryRes] = await Promise.all([
      getBrands(),
      getCategories(),
    ]);
    if (brandRes?.status && brandRes?.data?.brands?.length) {
      setBrands(brandRes.data.brands);
    }
    if (categoryRes?.status && categoryRes?.data?.categories?.length) {
      setCategories(categoryRes.data.categories);
    }
  }, []);

  useEffect(() => {
    loadBrandsAndCategories();
  }, [loadBrandsAndCategories]);

  const validateUploads = useCallback(() => {
    let isValid = true;
    if (!storeBanner) {
      setStoreBannerError("Store Banner Required");
      isValid = false;
    }
    return isValid;
  }, [storeBanner]);

  function handleForm(formData: any) {
    const isValid = validateUploads();
    if (!isValid) return;

    const description = (formData?.description || []).map(
      (item: any) => ({
        ...item,
        steps:
          typeof item.steps === "string"
            ? item.steps
              .split(/\r?\n/)
              .map((step: string) => step.trim())
              .filter(Boolean)
            : item.steps || [],
      })
    );

    const howToUse = (formData?.howToUse || []).map(
      (item: any) => ({
        ...item,
        steps:
          typeof item.steps === "string"
            ? item.steps
              .split(/\r?\n/)
              .map((step: string) => step.trim())
              .filter(Boolean)
            : item.steps || [],
      })
    );

    let formFields: any = {
      ...formData,
      description,
      howToUse,
    };

    if (storeIconFile) {
      formFields.cardIcon = storeIconFile;
    }
    if (storeBannerFile) {
      formFields.bannerImage = storeBannerFile;
    }

    const gameInfo = {
      question: formData?.question,
      steps:
        formData?.steps?.map((step: any, index: number) => {
          const file = stepImages[index]?.file;
          return {
            ...step,
            image: file instanceof File ? null : step?.image || null,
          };
        }) || [],
    };

    const stepImageFiles = Object.values(stepImages)
      .map((step: StepImage) => step.file)
      .filter((file): file is File => file !== null);

    if (isEditMode && store) {
      formFields = {
        ...formFields,

        media: {
          bannerImage:
            storeBannerFile instanceof File
              ? null
              : store?.media?.bannerImage || null,
          cardIcon:
            storeIconFile instanceof File
              ? null
              : store?.media?.cardIcon || null,
        },

        meta: {
          description,
          howToUse,
          priceDenomination: formData?.priceDenomination || [],
          gameInfo,
        },

        stepImages: stepImageFiles,
      };

      delete formFields.description;
      delete formFields.howToUse;
      delete formFields.priceDenomination;
      delete formFields.question;
      delete formFields.steps;
    } else {
      if (storeIconFile) {
        formFields.cardIcon = storeIconFile;
      }
      if (storeBannerFile) {
        formFields.bannerImage = storeBannerFile;
      }

      formFields.gameInfo = gameInfo;
      formFields.stepImages = stepImageFiles;
    }

    onAddStore(formFields);
  }

  async function handleUpload(
    files: File[],
    type: "banner" | "icon" | "bannerIcon"
  ) {
    dispatch(showLoader());
    if (files.length) {
      const file = files[0];
      const imageUrl = await convertFileToBase64(file);

      if (imageUrl) {
        if (type === "banner") {
          setStoreBanner(imageUrl);
          setStoreBannerFile(file);
        } else if (type === "icon") {
          setStoreIcon(imageUrl);
          setStoreIconFile(file);
        }
      }
    }
    dispatch(hideLoader());
  }

  function handleRedirection() {
    history.goBack();
  }

  useEffect(() => {
    if (submitFailed) {
      validateUploads();
    }
  }, [submitFailed, validateUploads]);

  useEffect(() => {
    if (!isEditMode) {
      reset();
      initialize({
        isPopular: false,
      });
      setStoreBannerError("");
      setStoreBanner("");
      setStoreIcon("");

      setStoreBannerFile(null);
      setStoreBannerFile(null);
    } else {
      if (isEditMode && store) {
        const {
          name,
          code,
          region,
          publisher,
          type,
          platformSupport,
          isPopular,
          priceStartingFrom,
          discountUpto,
          media,
          meta,
          seagmCatalogId,
          isStockAvailable,
          disclaimerText,
          brandId,
          categoryIds
        } = store;

        const description = (meta?.description || []).map(
          (item: any) => ({
            ...item,
            steps: Array.isArray(item.steps)
              ? item.steps.join("\n")
              : item.steps || "",
          })
        );

        const howToUse = (meta?.howToUse || []).map(
          (item: any) => ({
            ...item,
            steps: Array.isArray(item.steps)
              ? item.steps.join("\n")
              : item.steps || "",
          })
        );

        const initialData: any = {
          name,
          code,
          region,
          publisher,
          type,
          platformSupport,
          isPopular,
          priceStartingFrom,
          discountUpto,
          seagmCatalogId,
          brandId,
          categoryIds: parseJsonArray<number>(categoryIds),
          description,
          howToUse,
          priceDenomination: meta?.priceDenomination || [],
          question: meta?.gameInfo?.question || {},
          steps: meta?.gameInfo?.steps || {},
          isStockAvailable,
          disclaimerText
        };

        initialize(initialData);
        const newStepImages = Object.fromEntries(
          meta?.gameInfo?.steps?.map((step, index) => [
            index,
            { file: null, preview: step?.image },
          ]) || []
        );

        setStepImages(newStepImages);

        setStoreBanner(media?.bannerImage || "");
        setStoreIcon(media?.cardIcon || "");
        setStoreBannerFile(null);
        setStoreIconFile(null);
      }
    }
  }, [reset, isEditMode, store, initialize]);

  return (
    <Form fluid onSubmit={handleSubmit(handleForm)}>
      <div className="add-tournament-form">
        <div className="add-tournament-form-title-wrapper">
          <span className="form-back-btn" onClick={handleRedirection}>
            <Arrow fontSize={32} />
          </span>
          <h4>{isEditMode ? "Edit Card" : "Create Card"}</h4>
        </div>
        <div className="section-wrapper">
          <div className="section-title">Upload</div>
          <div className="tournaments-uploads-wrapper">
            <div className="tournament-icon">
              <UploadWithPreview
                onFileChange={(file) => handleUpload(file, "icon")}
                onRemove={() => setStoreIcon("")}
                label="Card Icon"
                placeholder="Add Card Icon"
                accept="image/*"
                dimension="[1:1 ratio | 1024px x 1024px]"
                cropResolution={{ width: 1024, height: 1024 }}
                value={storeIcon}
              />
            </div>

            <div className="tournament-banner">
              <UploadWithPreview
                onFileChange={(file) => handleUpload(file, "banner")}
                error={storeBannerError}
                onRemove={() => setStoreBanner("")}
                label="Card Banner"
                placeholder="Add Card Banner"
                accept="image/*"
                value={storeBanner}
                dimension="[1440px x 420px]"
              />
            </div>
          </div>
          <div className="section-title">Basic Info</div>
          <div className={clsx("tournament-grid-form-wrapper", "spacing")}>
            <Field
              name="name"
              component={InputWithLabel}
              placeholder="Enter  Name"
              label="Product Name"
              normalize={(value: string) => maxLength(value, 50)}
            />
            <Field
              name="code"
              component={InputWithLabel}
              placeholder="Enter  Code"
              label="Product Code"
              normalize={(value: string) => maxLength(value, 50)}
            />
            <Field
              name="seagmCatalogId"
              component={InputWithLabel}
              placeholder="Enter Seagm Catalog Id"
              label="Seagm Catalog Id"
              normalize={(value: string) => maxLength(value, 50)}
            />
            <Field
              cleanable={true}
              searchable={true}
              name="type"
              component={Select}
              placeholder="Select Type "
              label="Type"
              options={CARD_TYPE}
            />
            <Field
              cleanable={true}
              searchable={true}
              name="brandId"
              component={Select}
              placeholder="Select Brand"
              label="Brand"
              options={brandOptions}
            />
            <Field
              cleanable={true}
              searchable={true}
              name="categoryIds"
              component={MultiSelect}
              placeholder="Select Categories"
              label="Categories"
              options={categoryOptions}
            />
            <Field
              cleanable={true}
              searchable={true}
              name="region"
              component={Select}
              placeholder="Select Region "
              label="Region Group"
              options={regionOptions}
            />{" "}
            <Field
              cleanable={true}
              searchable={true}
              name="platformSupport"
              component={Select}
              placeholder="Select Platform "
              label="Platform Support"
              options={platformOptions}
            />
            <Field
              name="priceStartingFrom"
              component={InputWithLabel}
              placeholder="Price Starting From"
              label="Price Starting From"
              normalize={(value: string) => onlyNumber(value, 0, 1000, true)}
            />{" "}
            <Field
              name="discountUpto"
              component={InputWithLabel}
              placeholder="Discount Upto"
              label="Discount Upto"
              normalize={(value: string) => onlyNumber(value, 1, 100, true)}
            />
            <Field
              name={`isPopular`}
              component={Select}
              placeholder="Is Popular or Not"
              label="Is Popular"
              options={optionsReq}
            />
            <Field
              name={`isStockAvailable`}
              component={Select}
              placeholder="Stock Available "
              label="Is Stock Available"
              options={optionStock}
            />
          </div>
          <div className={clsx( "spacing")}>
            <Field
              name="disclaimerText"
              component={InputWithLabel}
              placeholder="Enter  Disclaimer Text"
              label="Disclaimer Text"
              normalize={(value: string) => maxLength(value, 1000)}
            />{" "}
          </div>
        </div>
        {selectedType !== "giftcard" && (
          <div className="section-wrapper">
            <div className="section-title">Game Info</div>
            <div className={clsx("tournament-grid-form-wrapper", "spacing")}>
              <Field
                name="question"
                component={InputWithLabel}
                placeholder="Enter Question"
                label="Question"
                normalize={(value: string) => maxLength(value, 50)}
              />
              <div className="tournament-grid-form-100">
                <FieldArray
                  name="steps"
                  props={{
                    title: "Steps",
                    setStepImages: setStepImages,
                    stepImages: stepImages,
                  }}
                  component={StoreGameInfo}
                />
              </div>
            </div>
          </div>
        )}
        {selectedType !== "giftcard" && (
          <div className="section-wrapper">
            <div className="tournament-grid-form-wrapper">
              <div className="tournament-grid-form-100">
                <FieldArray
                  name="priceDenomination"
                  props={{ title: "Price Denomination", buttonName: "Field" }}
                  component={StorePriceDenomination}
                />
              </div>
            </div>
            <div className="seperator" />
          </div>
        )}
        <div className="section-wrapper">
          <div className="tournament-grid-form-wrapper">
            <div className="tournament-grid-form-100">
              <FieldArray
                name="description"
                props={{ title: "Description", buttonName: "Field" }}
                component={StoreDescription}
              />
            </div>
          </div>
        </div>{" "}
        <div className="section-wrapper">
          <div className="tournament-grid-form-wrapper">
            <div className="tournament-grid-form-100">
              <FieldArray
                name="howToUse"
                props={{ title: "How To Use", buttonName: "Field" }}
                component={StoreDescription}
              />
            </div>
          </div>
          <div className="create-tourney-btn">
            <Button appearance="primary" type="submit">
              {isEditMode ? "Update Card" : "Create Card"}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default reduxForm<{}, AddStoreFormProps>({
  form: "add-store",
  validate: (values, props) => validate(values, props),
  onSubmitFail: handleSubmitFail,
})(AddStoreForm);
